import { Button } from "@components/Button";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { SelectionInput, TextInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { MainLayout, MainLayoutProps } from "@components/MainLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { State, UserEmploymentStatus } from "@ctypes";
import { useIndustries, useModal, useOccupations, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { Separator } from "@routes/VerifiedRoute/styles";
import { ChangeDetailsModal } from "./ChangeDetailsModal";
import { status } from "./content";
import { styles } from "./styles";

export const EmploymentInformationRoute = () => {
  const { loading: industryLoading, values: industryValues, keys: industryKeys } = useIndustries();

  const {
    loading: occupationLoading,
    values: occupationValues,
    keys: occupationKeys,
  } = useOccupations();

  const { user, loading: userLoading, error } = useUser();

  const { show } = useModal();

  const loading = industryLoading || occupationLoading || userLoading;

  if (loading) return <Loader />;
  if (error) return <ErrorComponent />;

  const employmentStatus = user?.userFinancialProfile?.employmentStatus;

  const defaultStateIndex = Object.values(State).findIndex(
    (s) => s === user?.userFinancialProfile?.employerAddressState,
  );

  const props: MainLayoutProps = {
    pageHeader: i18n.t(Translations.EmploymentInformationRouteTitle),
  };

  const defaultIndustryIndex = (() => {
    const index = industryKeys.findIndex(
      (i) => i === user?.userFinancialProfile?.occupationIndustry,
    );

    return index >= 0 ? index : 0;
  })();

  const defaultOccupationIndex = (() => {
    const index = occupationKeys.findIndex(
      (i) => i === user?.userFinancialProfile?.occupationTitle,
    );

    return index >= 0 ? index : 0;
  })();

  const shouldDisplayEmployerFields = [
    UserEmploymentStatus.Employed,
    UserEmploymentStatus.SelfEmployed,
  ].includes(employmentStatus ?? UserEmploymentStatus.Employed);

  const showChangeDetailsModal = () => {
    show(<ChangeDetailsModal />);
  };

  return (
    <MainLayout {...props}>
      <TextInput
        label={i18n.t(Translations.EmploymentRouteStatusInputLabel)}
        value={employmentStatus ? status[employmentStatus] : ""}
        style={styles.input}
        readOnly
      />
      <Spacing size={SpacingSizes.S} />

      {shouldDisplayEmployerFields ? (
        <>
          <TextInput
            label={i18n.t(Translations.EmploymentRouteNameInputLabel)}
            value={user?.userFinancialProfile?.employerName ?? ""}
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />

          <TextInput
            label={i18n.t(Translations.EmploymentRouteIndustryLabel)}
            value={defaultIndustryIndex != null ? industryValues[defaultIndustryIndex] : undefined}
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />

          <TextInput
            label={i18n.t(Translations.EmploymentRouteOccupationLabel)}
            value={
              defaultOccupationIndex != null ? occupationValues[defaultOccupationIndex] : undefined
            }
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />

          <TextInput
            label={i18n.t(Translations.EmployerAutofillRouteAddressLabel)}
            value={user?.userFinancialProfile?.employerAddressStreet ?? ""}
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />

          <TextInput
            label={i18n.t(Translations.AddressFormUnitNoLabel)}
            value={user?.userFinancialProfile?.employerAddressUnitNo ?? ""}
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />

          <Row>
            <TextInput
              label={i18n.t(Translations.AddressFormCityLabel)}
              value={user?.userAddress?.city ?? ""}
              readOnly
              style={styles.input}
            />

            <Separator />

            <SelectionInput
              label={i18n.t(Translations.AddressFormStateLabel)}
              items={Object.values(State)}
              defaultIndex={defaultStateIndex >= 0 ? defaultStateIndex : 0}
              readOnly
            />
          </Row>
          <Spacing size={SpacingSizes.S} />

          <TextInput
            label={i18n.t(Translations.AddressFormZipCodeLabel)}
            value={user?.userFinancialProfile?.employerAddressPostalCode ?? ""}
            style={styles.input}
            readOnly
          />
          <Spacing size={SpacingSizes.S} />
        </>
      ) : null}

      <Button
        title={i18n.t(Translations.EmploymentInformationRoutePrimaryCTA)}
        onClick={showChangeDetailsModal}
      />
      <Spacing size={SpacingSizes.S} />
    </MainLayout>
  );
};
